/**
 * In-tab coordination for excerpts (#277).
 *
 * - Every excerpt-capable editor registers here under its document
 *   (`task:<id>`, ...). That is what lets an edit made inside an embed go
 *   straight into the open source editor - its own autosave persists it, so no
 *   clash is possible - and lets an embed show the source's text live while
 *   it is being typed.
 * - A shared cache of excerpt views (GET /api/excerpts?ids=), loaded in
 *   batches, so a page full of embeds makes one request.
 * - Source editors get their live-excerpt set (which marks to show) from
 *   GET /api/excerpts/by-source, refreshed after anything that changes it.
 *
 * When the source is not open in this tab, an edit is applied to a fresh copy
 * of the source document and saved with its revision (#288); a clash retries
 * once on the newer copy.
 */

import { createContext, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import type { Editor, JSONContent } from '@tiptap/core';
import { EditorState } from '@tiptap/pm/state';
import { Slice, type Schema } from '@tiptap/pm/model';
import { apiGet, apiPatch, CONTENT_SAVED_EVENT } from '../lib/api';
import { StaleContentError } from '../lib/contentRevs';
import { HISTORY_CHANGED_EVENT } from '../lib/history';
import { getExcerpts, listBySource, SOURCE_ROUTES, type ExcerptKind, type ExcerptView, type SourceType } from '../lib/excerpts';
import { findExcerptRange, setExcerptTaskStates, setLiveExcerpts, type TaskEndState } from '../components/editor/ExcerptMark';
import { fetchTaskStatus } from '../lib/taskStatusCache';
import { TASKS_CHANGED_EVENT } from '../lib/taskEvents';

export interface DocRef { type: SourceType; id: string }
const keyOf = (r: { type: string; id: string }) => `${r.type}:${r.id}`;

/** The marked range of `id` in a document, as a doc-shaped fragment; null when absent. */
export function fragmentFromDoc(doc: EditorState['doc'], id: string): JSONContent | null {
  const range = findExcerptRange(doc, id);
  if (!range) return null;
  // cut keeps the enclosing blocks, trimmed - the same shape the server derives.
  return doc.cut(range.from, range.to).toJSON() as JSONContent;
}

/**
 * A transaction replacing excerpt `id` in `state` with `fragment`. All the new
 * text carries the excerpt's mark, so typing at the end of an embed stays
 * inside the excerpt. Null when the excerpt is no longer in the document.
 */
export function replaceExcerpt(state: EditorState, id: string, kind: ExcerptKind, fragment: JSONContent) {
  const range = findExcerptRange(state.doc, id);
  if (!range) return null;
  const schema = state.schema;
  const fragDoc = schema.nodeFromJSON({ type: 'doc', content: fragment.content?.length ? fragment.content : [{ type: 'paragraph' }] });
  const marked = EditorState.create({ schema, doc: fragDoc }).tr;
  marked.addMark(0, marked.doc.content.size, schema.marks.excerpt.create({ id, kind }));
  return state.tr.replace(range.from, range.to, Slice.maxOpen(marked.doc.content));
}

type Listener = () => void;

export class ExcerptStore {
  private editors = new Map<string, Editor>();
  private views = new Map<string, ExcerptView | null>();
  private sourceCounts = new Map<string, number>();
  private pending = new Set<string>();
  private loading = false;
  private listeners = new Set<Listener>();
  private version = 0;

  subscribe = (l: Listener) => { this.listeners.add(l); return () => { this.listeners.delete(l); }; };
  getVersion = () => this.version;
  private emit() { this.version++; for (const l of this.listeners) l(); }

  registerEditor(ref: DocRef, editor: Editor): () => void {
    const key = keyOf(ref);
    this.listen();
    this.editors.set(key, editor);
    const onChange = () => this.emit();
    editor.on('update', onChange);
    void this.refreshSource(ref);
    this.emit();
    return () => {
      editor.off('update', onChange);
      if (this.editors.get(key) === editor) this.editors.delete(key);
      this.emit();
    };
  }

  editorFor(ref: { type: string; id: string }): Editor | undefined {
    const e = this.editors.get(keyOf(ref));
    return e && !e.isDestroyed ? e : undefined;
  }

  /** Re-reads which excerpts are live in an open source document. */
  async refreshSource(ref: DocRef): Promise<void> {
    try {
      const list = await listBySource(ref.type, ref.id);
      for (const v of list) this.views.set(v.id, v);
      this.sourceCounts.set(keyOf(ref), list.length);
      const editor = this.editorFor(ref);
      if (editor) setLiveExcerpts(editor, new Map(list.map((v) => [v.id, v.kind])));
      this.emit();
      await this.refreshTaskStates(ref);
    } catch {
      // A failed refresh leaves the marks as they were; the next change retries.
    }
  }

  /** Shows whether each task excerpt in an open source has its task done (#324). */
  async refreshTaskStates(ref: DocRef): Promise<void> {
    const tasks = [...this.views.values()].filter((v): v is ExcerptView =>
      !!v && v.kind === 'task' && !!v.linked_entity_id && v.source.type === ref.type && v.source.id === ref.id);
    const statuses = await Promise.all(tasks.map((v) => fetchTaskStatus(v.linked_entity_id!)));
    const states = new Map<string, TaskEndState>();
    tasks.forEach((v, i) => { const s = statuses[i]; if (s === 'done' || s === 'cancelled') states.set(v.id, s); });
    const editor = this.editorFor(ref);
    if (editor) setExcerptTaskStates(editor, states);
  }

  /** A task changed somewhere: redraw the task icons in every open source. */
  onTasksChanged(): void {
    for (const key of this.editors.keys()) {
      const i = key.indexOf(':');
      void this.refreshTaskStates({ type: key.slice(0, i) as SourceType, id: key.slice(i + 1) });
    }
  }

  private onTasksEvent = () => this.onTasksChanged();
  private listening = false;

  /** Stops listening for task changes (tests; the provider lives as long as the tab). */
  dispose(): void {
    if (this.listening) window.removeEventListener(TASKS_CHANGED_EVENT, this.onTasksEvent);
    this.listening = false;
  }

  private listen(): void {
    if (this.listening || typeof window === 'undefined') return;
    window.addEventListener(TASKS_CHANGED_EVENT, this.onTasksEvent);
    this.listening = true;
  }

  /** Live excerpts in a source document, as last read. */
  sourceCount(ref: { type: string; id: string }): number {
    return this.sourceCounts.get(keyOf(ref)) ?? 0;
  }

  view(id: string): ExcerptView | null | undefined {
    if (!this.views.has(id)) this.request(id);
    return this.views.get(id);
  }

  /** The excerpt's text: live from its open source editor, else the server's copy. */
  fragment(id: string): JSONContent | null {
    const v = this.views.get(id);
    if (!v) return null;
    const editor = this.editorFor(v.source);
    return (editor && fragmentFromDoc(editor.state.doc, id)) ?? v.fragment;
  }

  private request(id: string) {
    this.pending.add(id);
    if (this.loading) return;
    this.loading = true;
    queueMicrotask(() => { void this.flush(); });
  }

  private async flush() {
    const ids = [...this.pending].slice(0, 200);
    for (const id of ids) this.pending.delete(id);
    try {
      const got = await getExcerpts(ids);
      const byId = new Map(got.map((v) => [v.id, v]));
      // A view a source refresh filled in meanwhile is at least as fresh.
      for (const id of ids) if (!this.views.get(id)) this.views.set(id, byId.get(id) ?? null);
    } catch {
      for (const id of ids) if (!this.views.get(id)) this.views.set(id, null);
    }
    this.loading = false;
    if (this.pending.size) this.request([...this.pending][0]);
    this.emit();
  }

  /** Forget cached views so the next read refetches; all of them when no ids are given. */
  invalidate(ids?: string[]) {
    if (!ids) this.views.clear();
    else for (const id of ids) this.views.delete(id);
    this.emit();
  }

  /**
   * After a document autosave, what was shown from it may have changed on the
   * server (a new excerpt turns active, a fragment is re-derived): refetch
   * those views and the document's live set.
   */
  onContentSaved(path: string) {
    const m = /^\/(tasks|notes|meeting-instances|scratchpads)\/([^/?#]+)$/.exec(path);
    if (!m) return;
    const type = ({ tasks: 'task', notes: 'note', 'meeting-instances': 'meeting_instance', scratchpads: 'scratchpad' } as const)[m[1] as 'tasks'];
    const stale = [...this.views.values()].filter((v) => v && v.source.type === type && v.source.id === m[2]).map((v) => v!.id);
    if (stale.length) this.invalidate(stale);
    if (this.editorFor({ type, id: m[2] })) void this.refreshSource({ type, id: m[2] });
  }

  /**
   * Writes an edit made in a destination back to the source. `schema` is the
   * calling editor's: every Dossier editor shares one.
   */
  async applyEdit(excerptId: string, fragment: JSONContent, schema: Schema): Promise<void> {
    const v = this.views.get(excerptId);
    if (!v) throw new Error('Excerpt not loaded');
    const source = this.editorFor(v.source);
    if (source) {
      const tr = replaceExcerpt(source.state, excerptId, v.kind, fragment);
      if (!tr) throw new Error('Excerpt is no longer in its source');
      source.view.dispatch(tr);
      return;
    }
    const route = SOURCE_ROUTES[v.source.type as SourceType];
    for (let attempt = 0; attempt < 2; attempt++) {
      const item = await apiGet<Record<string, unknown>>(`${route.path}/${v.source.id}`);
      const raw = item[route.field];
      const json = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const state = EditorState.create({ schema, doc: schema.nodeFromJSON(json) });
      const tr = replaceExcerpt(state, excerptId, v.kind, fragment);
      if (!tr) throw new Error('Excerpt is no longer in its source');
      try {
        await apiPatch(`${route.path}/${v.source.id}`, { [route.field]: JSON.stringify(tr.doc.toJSON()) }, { announceStale: false });
        this.invalidate([excerptId]);
        return;
      } catch (err) {
        if (!(err instanceof StaleContentError) || attempt === 1) throw err;
      }
    }
  }
}

const Ctx = createContext<ExcerptStore | null>(null);

export function ExcerptSyncProvider({ children }: { children: ReactNode }) {
  const store = useMemo(() => new ExcerptStore(), []);
  useEffect(() => {
    const onSaved = (e: Event) => store.onContentSaved((e as CustomEvent<{ path: string }>).detail?.path ?? '');
    // An undo/redo can restore or remove excerpts anywhere (#278).
    const onHistory = () => store.invalidate();
    window.addEventListener(CONTENT_SAVED_EVENT, onSaved);
    window.addEventListener(HISTORY_CHANGED_EVENT, onHistory);
    return () => {
      window.removeEventListener(CONTENT_SAVED_EVENT, onSaved);
      window.removeEventListener(HISTORY_CHANGED_EVENT, onHistory);
      store.dispose();
    };
  }, [store]);
  return <Ctx.Provider value={store}>{children}</Ctx.Provider>;
}

/** The store, or null outside the provider (tests, read-only previews). */
export function useExcerptStore(): ExcerptStore | null {
  return useContext(Ctx);
}

/** Re-renders whenever the store changes. */
export function useExcerptStoreVersion(store: ExcerptStore | null): number {
  return useSyncExternalStore(store?.subscribe ?? noopSubscribe, store?.getVersion ?? zero);
}
const noopSubscribe = () => () => {};
const zero = () => 0;

/** Registers an editor as the open copy of its document while mounted. */
export function useRegisterExcerptEditor(ref: DocRef | undefined, editor: Editor | null) {
  const store = useExcerptStore();
  const key = ref ? keyOf(ref) : '';
  useEffect(() => {
    if (!store || !ref || !editor) return;
    return store.registerEditor(ref, editor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, key, editor]);
}
